import { useCallback, useId, useState } from 'react'
import { Form } from 'radix-ui'
import { StepProps, ClaimFormSchemaProperty } from '../../types'
import { FormFloatingFooter } from '@Components/claim-flow/form-floating-footer'
import { Button } from '@Components/button/button'
import { Selector } from '@Components/selector/selector'
import { PreviousButton } from '@Components/previous-button/previous-button'

import './generic-schema-step.scss'

const CLASS_NAME = 'generic-schema-step'

function formatLabel(key: string): string {
  return key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1')
}

function getNestedValue(data: Record<string, unknown>, path: string[]): unknown {
  let current: unknown = data
  for (const key of path) {
    if (current === null || current === undefined || typeof current !== 'object') {
      return undefined
    }
    current = (current as Record<string, unknown>)[key]
  }
  return current
}

function setNestedValue(target: Record<string, unknown>, path: string[], value: unknown): void {
  let current = target
  for (let i = 0; i < path.length - 1; i++) {
    const key = path[i]
    if (!(key in current) || typeof current[key] !== 'object' || current[key] === null) {
      current[key] = {}
    }
    current = current[key] as Record<string, unknown>
  }
  current[path[path.length - 1]] = value
}

type FieldRendererProps = {
  fieldKey: string
  property: ClaimFormSchemaProperty
  required: boolean
  path: string[]
  formData: Record<string, unknown>
  selectorValues: Record<string, string>
  onSelectorChange: (fieldName: string, value: string) => void
}

const GenericField = ({
  fieldKey,
  property,
  required,
  path,
  formData,
  selectorValues,
  onSelectorChange,
}: FieldRendererProps) => {
  const labelId = useId()
  const fieldName = path.join('.')
  const existingValue = getNestedValue(formData, path)
  const label = `${property.description ?? formatLabel(fieldKey)}${required ? ' *' : ''}`

  if (property.type === 'array') {
    return null
  }

  if (property.type === 'object' && property.properties) {
    return (
      <fieldset className={`${CLASS_NAME}__fieldset`}>
        <legend className={`${CLASS_NAME}__fieldset-title`}>{formatLabel(fieldKey)}</legend>
        {Object.entries(property.properties).map(([subKey, subProp]) => {
          const isSubRequired = property.required?.includes(subKey) ?? false
          return (
            <GenericField
              key={subKey}
              fieldKey={subKey}
              property={subProp}
              required={isSubRequired}
              path={[...path, subKey]}
              formData={formData}
              selectorValues={selectorValues}
              onSelectorChange={onSelectorChange}
            />
          )
        })}
      </fieldset>
    )
  }

  if (property.type === 'boolean') {
    return (
      <Form.Field name={fieldName} className={`${CLASS_NAME}__checkbox-field`}>
        <Form.Control
          type="checkbox"
          name={fieldName}
          defaultChecked={(existingValue as boolean) ?? false}
        />
        <Form.Label className={`${CLASS_NAME}__label`}>{label}</Form.Label>
      </Form.Field>
    )
  }

  if (property.type === 'string' && property.enum) {
    const options = property.enum.map((val) => ({ label: val, value: val }))
    return (
      <Form.Field name={fieldName} className={`${CLASS_NAME}__field`}>
        <Form.Label id={labelId} className={`${CLASS_NAME}__label`}>
          {label}
        </Form.Label>
        {required && (
          <Form.Message className={`${CLASS_NAME}__message`} match="valueMissing">
            Please select a value
          </Form.Message>
        )}
        <Form.Control asChild name={fieldName} required={required}>
          <Selector<string>
            ariaLabelledBy={labelId}
            placeHolderText={`Select ${formatLabel(fieldKey)}...`}
            selectables={options}
            value={selectorValues[fieldName] ?? (existingValue as string) ?? undefined}
            onValueChange={(value) => onSelectorChange(fieldName, value)}
          />
        </Form.Control>
      </Form.Field>
    )
  }

  const inputType = property.type === 'number' || property.type === 'integer' ? 'number' : 'text'

  return (
    <Form.Field name={fieldName} className={`${CLASS_NAME}__field`}>
      <Form.Label className={`${CLASS_NAME}__label`}>{label}</Form.Label>
      {required && (
        <Form.Message className={`${CLASS_NAME}__message`} match="valueMissing">
          Please enter a value
        </Form.Message>
      )}
      <Form.Control
        type={inputType}
        name={fieldName}
        required={required}
        defaultValue={existingValue != null ? String(existingValue) : ''}
        className={`${CLASS_NAME}__input-control`}
      />
    </Form.Field>
  )
}

export const GenericSchemaStep = ({
  schema,
  formData,
  onSubmit,
  onBack,
  showBack = true,
}: StepProps) => {
  const [selectorValues, setSelectorValues] = useState<Record<string, string>>({})

  const onSelectorChange = useCallback((fieldName: string, value: string) => {
    setSelectorValues((prev) => ({ ...prev, [fieldName]: value }))
  }, [])

  const handleSubmit = useCallback(
    (formDataEvent: FormData) => {
      const result: Record<string, unknown> = {}

      for (const [dotPath, value] of formDataEvent.entries()) {
        const keys = dotPath.split('.')
        setNestedValue(result, keys, value)
      }

      // Handle checkboxes: FormData only includes checked checkboxes,
      // so we need to set unchecked booleans to false
      const setBooleanDefaults = (
        properties: Record<string, ClaimFormSchemaProperty>,
        parentPath: string[],
      ) => {
        for (const [key, prop] of Object.entries(properties)) {
          const currentPath = [...parentPath, key]
          if (prop.type === 'boolean') {
            const existing = getNestedValue(result, currentPath)
            if (existing === 'on') {
              setNestedValue(result, currentPath, true)
            } else if (existing === undefined) {
              setNestedValue(result, currentPath, false)
            }
          }
          if (prop.type === 'object' && prop.properties) {
            setBooleanDefaults(prop.properties, currentPath)
          }
        }
      }

      // Convert number/integer strings to actual numbers
      const convertNumbers = (
        properties: Record<string, ClaimFormSchemaProperty>,
        parentPath: string[],
      ) => {
        for (const [key, prop] of Object.entries(properties)) {
          const currentPath = [...parentPath, key]
          if (prop.type === 'number' || prop.type === 'integer') {
            const val = getNestedValue(result, currentPath)
            if (typeof val === 'string' && val !== '') {
              setNestedValue(result, currentPath, Number(val))
            }
          }
          if (prop.type === 'object' && prop.properties) {
            convertNumbers(prop.properties, currentPath)
          }
        }
      }

      for (const [sectionKey, sectionProp] of Object.entries(schema.properties)) {
        if (sectionProp.properties) {
          setBooleanDefaults(sectionProp.properties, [sectionKey])
          convertNumbers(sectionProp.properties, [sectionKey])
        }
      }

      // Merge selector values into result
      for (const [dotPath, value] of Object.entries(selectorValues)) {
        const keys = dotPath.split('.')
        setNestedValue(result, keys, value)
      }

      onSubmit(result)
    },
    [schema, onSubmit, selectorValues],
  )

  return (
    <Form.Root asChild action={handleSubmit}>
      <Form.Form className={CLASS_NAME}>
        {Object.entries(schema.properties).map(([sectionKey, sectionProp]) => (
          <section key={sectionKey}>
            <h2 className={`${CLASS_NAME}__section-title`}>{formatLabel(sectionKey)}</h2>
            {sectionProp.properties &&
              Object.entries(sectionProp.properties).map(([fieldKey, fieldProp]) => {
                const isRequired = sectionProp.required?.includes(fieldKey) ?? false
                return (
                  <GenericField
                    key={fieldKey}
                    fieldKey={fieldKey}
                    property={fieldProp}
                    required={isRequired}
                    path={[sectionKey, fieldKey]}
                    formData={formData}
                    selectorValues={selectorValues}
                    onSelectorChange={onSelectorChange}
                  />
                )
              })}
          </section>
        ))}

        <FormFloatingFooter>
          <section className="floating-footer__buttons">
            <PreviousButton show={showBack} onClick={onBack} />
            <Form.Submit asChild>
              <Button className="floating-footer__next-button" disabled={false} onClick={() => {}}>
                Next
              </Button>
            </Form.Submit>
          </section>
        </FormFloatingFooter>
      </Form.Form>
    </Form.Root>
  )
}
